import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "./lib/supabase";

type FeedbackKind = "improvement" | "bug" | "usability" | "other";
type FeedbackPriority = "low" | "normal" | "high" | "blocking";

type FeedbackReceipt = {
  id: string;
  email_delivery_status: "prepared" | "queued" | "sent" | "delivered" | "failed";
  support_email_snapshot: string;
  title: string;
  description: string;
};

export function ApplicationFeedbackDialog({
  open,
  onClose,
  role,
  screenKey,
  screenLabel,
}: {
  open: boolean;
  onClose: () => void;
  role: string;
  screenKey: string;
  screenLabel: string;
}) {
  const [kind, setKind] = useState<FeedbackKind>("improvement"),
    [priority, setPriority] = useState<FeedbackPriority>("normal"),
    [title, setTitle] = useState(""),
    [description, setDescription] = useState(""),
    [submitting, setSubmitting] = useState(false),
    [error, setError] = useState(""),
    [receipt, setReceipt] = useState<FeedbackReceipt | null>(null);
  const titleInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    setReceipt(null);
    const timer = window.setTimeout(() => titleInput.current?.focus(), 30);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, submitting]);

  const deviceContext = useMemo(
    () => ({
      role,
      user_agent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        pixel_ratio: window.devicePixelRatio,
      },
      native_platform: Capacitor.getPlatform(),
      native: Capacitor.isNativePlatform(),
      path: `${location.pathname}${location.search}`,
      captured_at: new Date().toISOString(),
    }),
    [open, role],
  );

  if (!open) return null;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Lire les valeurs réellement présentes dans le formulaire au moment du
    // clic. Cela évite qu'une saisie très rapide soit soumise avec l'ancien
    // état React sur un appareil ou un navigateur ralenti.
    const formData = new FormData(event.currentTarget),
      submittedKind = String(formData.get("feedback_kind") || kind) as FeedbackKind,
      submittedPriority = String(formData.get("feedback_priority") || priority) as FeedbackPriority,
      submittedTitle = String(formData.get("feedback_title") || title).trim(),
      submittedDescription = String(formData.get("feedback_description") || description).trim();
    if (submittedTitle.length < 3 || submittedDescription.length < 10) {
      setError("Ajoutez un titre précis et au moins une phrase d’explication.");
      return;
    }
    setSubmitting(true);
    setError("");
    const result = await supabase!.rpc("submit_application_feedback", {
      p_feedback_kind: submittedKind,
      p_priority: submittedPriority,
      p_title: submittedTitle,
      p_description: submittedDescription,
      p_screen_key: screenKey,
      p_screen_label: screenLabel,
      p_platform: Capacitor.getPlatform(),
      p_app_version: import.meta.env.VITE_APP_VERSION || "developpement",
      p_device_context: deviceContext,
    });
    setSubmitting(false);
    if (result.error) {
      setError(`Le retour n’a pas pu être enregistré : ${result.error.message}`);
      return;
    }
    const saved = (Array.isArray(result.data) ? result.data[0] : result.data) as FeedbackReceipt;
    setReceipt(saved);
  };

  const openPreparedEmail = () => {
    if (!receipt) return;
    const subject = `[ARD Manager] Retour application — ${receipt.title}`;
    const body = [
      `Référence : ${receipt.id}`,
      `Écran : ${screenLabel}`,
      "",
      receipt.description,
    ].join("\n");
    location.href = `mailto:${encodeURIComponent(receipt.support_email_snapshot)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const deliveryMessage = receipt
    ? receipt.email_delivery_status === "queued"
      ? "Votre retour est enregistré et placé dans la file d’envoi par e-mail."
      : receipt.email_delivery_status === "sent" || receipt.email_delivery_status === "delivered"
        ? "Votre retour est enregistré et l’e-mail a été envoyé."
        : "Votre retour est enregistré sans perte. L’envoi automatique n’est pas encore connecté : l’e-mail est préparé ci-dessous."
    : "";

  return (
    <div className="portal-overlay application-feedback-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !submitting) onClose();
    }}>
      <section className="portal-detail application-feedback-dialog" role="dialog" aria-modal="true" aria-labelledby="application-feedback-title">
        <header>
          <div>
            <span className="eyebrow">Amélioration continue</span>
            <h2 id="application-feedback-title">Proposer une amélioration ou signaler un défaut</h2>
            <p>L’écran, le profil et l’appareil sont ajoutés automatiquement pour faciliter le traitement.</p>
          </div>
          <button type="button" className="ghost dialog-close" aria-label="Fermer" onClick={onClose} disabled={submitting}>×</button>
        </header>
        {receipt ? (
          <div className="application-feedback-receipt">
            <p className="notice success" role="status">{deliveryMessage}</p>
            <dl>
              <div><dt>Référence</dt><dd>{receipt.id}</dd></div>
              <div><dt>Écran</dt><dd>{screenLabel}</dd></div>
            </dl>
            <div className="dialog-actions">
              {receipt.email_delivery_status === "prepared" && (
                <button type="button" className="primary" onClick={openPreparedEmail}>Ouvrir l’e-mail préparé</button>
              )}
              <button type="button" className="ghost" onClick={() => {
                setTitle("");
                setDescription("");
                setReceipt(null);
              }}>Faire un autre retour</button>
              <button type="button" className="ghost" onClick={onClose}>Fermer</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="application-feedback-context">
              <span>Écran</span><strong>{screenLabel}</strong>
              <span>Profil</span><strong>{role === "admin" ? "Administrateur" : role === "client" ? "Client" : role === "employee" ? "Employé" : "Sous-traitant"}</strong>
            </div>
            <div className="form-grid two-columns">
              <label>
                Type de retour
                <select name="feedback_kind" value={kind} onChange={(event) => setKind(event.target.value as FeedbackKind)}>
                  <option value="improvement">Idée d’amélioration</option>
                  <option value="bug">Défaut ou bug</option>
                  <option value="usability">Difficulté d’utilisation</option>
                  <option value="other">Autre retour</option>
                </select>
              </label>
              <label>
                Importance ressentie
                <select name="feedback_priority" value={priority} onChange={(event) => setPriority(event.target.value as FeedbackPriority)}>
                  <option value="low">Confort</option>
                  <option value="normal">Normale</option>
                  <option value="high">Importante</option>
                  <option value="blocking">Bloquante</option>
                </select>
              </label>
            </div>
            <label>
              Résumé
              <input name="feedback_title" ref={titleInput} value={title} maxLength={140} onChange={(event) => setTitle(event.target.value)} placeholder="Ex. Le bouton de validation est difficile à trouver" required />
            </label>
            <label>
              Que faudrait-il améliorer ou corriger ?
              <textarea name="feedback_description" value={description} maxLength={5000} rows={7} onChange={(event) => setDescription(event.target.value)} placeholder="Décrivez ce que vous faisiez, ce qui s’est passé et ce que vous attendiez." required />
            </label>
            {error && <p className="notice error" role="alert">{error}</p>}
            <div className="dialog-actions">
              <button type="button" className="ghost" onClick={onClose} disabled={submitting}>Annuler</button>
              <button type="submit" className="primary" disabled={submitting}>{submitting ? "Enregistrement…" : "Envoyer le retour"}</button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
